import { get } from "lodash";
import { Eye, X } from "lucide-react";
import { memo, useMemo } from "react";

import { CARGO_TYPES, WAGON_TYPES } from "../lib/utils";

import { OrderPageAllTradeCargoResponseDTO } from "@/common/api";
import {
  TruncateTooltip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
} from "@/common/components";
import { formatDatetime, getStatusData } from "@/common/lib";
import { CargoRequest, TradingOffer } from "@/common/model/schemas/replit";

interface TradingCargoTableProps {
  requests: OrderPageAllTradeCargoResponseDTO[];
  isLoading?: boolean;
  userId?: string;
  allTradingOffers?: TradingOffer[];
  onViewDetails: (request: OrderPageAllTradeCargoResponseDTO) => void;
  onExtendTrading?: (requestId: string) => void;
  onEndTrading?: (requestId: string) => void;
  onStartRetrade?: (request: CargoRequest) => void;
  onCancelRequest?: (requestId: string) => void;
}

export const TradingCargoTable = memo(function TradingCargoTable({
  requests,
  isLoading,
  userId,
  allTradingOffers,
  onViewDetails,
  onCancelRequest,
}: TradingCargoTableProps) {
  const participantsCountByRequestId = useMemo(() => {
    const result: Record<string, number> = {};
    const offersByRequest = new Map<string, Set<string>>();

    (allTradingOffers ?? []).forEach((offer: TradingOffer) => {
      if (!offer.cargoRequestId || !offer.offererId) return;

      const key = String(offer.cargoRequestId);
      const existingSet = offersByRequest.get(key);

      if (existingSet) {
        existingSet.add(String(offer.offererId));
      } else {
        offersByRequest.set(key, new Set([String(offer.offererId)]));
      }
    });

    offersByRequest.forEach((offerers, requestId) => {
      result[requestId] = offerers.size;
    });

    return result;
  }, [allTradingOffers]);

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p className="caption">Загрузка заявок...</p>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="caption">Заявки на модерации отсутствуют</p>
      </div>
    );
  }
  return (
    <Table className="p-0 w-[calc(100vw-72px)] md:w-[calc(100vw-336px)]">
      <TableHeader>
        <TableRow>
          <TableHead>№ заявки</TableHead>
          <TableHead>Период проведения</TableHead>
          <TableHead>Этап</TableHead>
          <TableHead>Период этапа сессии</TableHead>
          <TableHead>Станция отправления (дорога)</TableHead>
          <TableHead>Количество участников</TableHead>
          <TableHead>Груз</TableHead>
          <TableHead>Вагоны</TableHead>
          <TableHead>Мое участие</TableHead>
          <TableHead>Действия</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {requests.map((request: OrderPageAllTradeCargoResponseDTO) => {
          // Используем participantsCount из DTO или подсчитываем из offers
          const participantsCount =
            request.participantsCount ?? participantsCountByRequestId[String(request.id)] ?? 0;

          // Используем myParticipation из DTO или проверяем через offers
          const hasMyParticipation =
            request.myParticipation ??
            (userId &&
              !!allTradingOffers?.some(
                (offer) => offer.cargoRequestId === request.id && offer.offererId === userId,
              ));

          const handleRowClick = () => {
            if (onViewDetails && request.id) {
              onViewDetails(request);
            }
          };

          return (
            <TableRow
              key={request.id}
              className="cursor-pointer hover:bg-gray-50"
              onClick={handleRowClick}
            >
              <TableCell className="font-medium">{request.orderNumber || "Не указано"}</TableCell>
              <TableCell className="text-sm text-nowrap">
                {request.tradeBegin && (
                  <p className="text-sm text-nowrap">c {formatDatetime(request.tradeBegin)}</p>
                )}
                {request.tradeEnd && (
                  <p className="text-sm text-nowrap">по {formatDatetime(request.tradeEnd)}</p>
                )}
                {!request.tradeBegin && !request.tradeEnd && <p>Не указано</p>}
              </TableCell>
              <TableCell className="text-sm">
                <Badge className={getStatusData(request.status).color}>
                  {getStatusData(request.status).label}
                </Badge>
              </TableCell>
              <TableCell className="text-sm">
                {request.stageBegin && (
                  <p className="text-sm text-nowrap">c {formatDatetime(request.stageBegin)}</p>
                )}
                {request.endBegin && (
                  <p className="text-sm text-nowrap">по {formatDatetime(request.endBegin)}</p>
                )}
                {!request.stageBegin && !request.endBegin && <p> - </p>}
              </TableCell>
              <TableCell>
                <TruncateTooltip
                  text={request.loadingStation ?? "-"}
                  className="text-sm font-medium text-center"
                />
                <p className="text-sm font-medium">
                  {request.loadingRoad && `(${request.loadingRoad})`}
                </p>
              </TableCell>
              <TableCell>
                <div className="text-center">
                  <div>{participantsCount}</div>
                </div>
              </TableCell>
              <TableCell>
                <div className="text-sm">
                  <TruncateTooltip
                    text={
                      request.cargoName
                        ? get(CARGO_TYPES, request.cargoName) || request.cargoName
                        : "Не указано"
                    }
                    className="font-medium"
                  />
                  <div className="text-sm">
                    {request.cargoWeight ? `(${request.cargoWeight} т.)` : "-"}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div className="text-sm">
                  {request.wagonsType && (
                    <div className="font-medium">
                      {get(WAGON_TYPES, request.wagonsType) || request.wagonsType}
                    </div>
                  )}
                  {request.totalWagons && <div className="">({request.totalWagons} в.)</div>}
                  {!request.totalWagons && !request.wagonsType && (
                    <div className="text-sm text-muted-foreground">Не указано</div>
                  )}
                </div>
              </TableCell>
              <TableCell>{hasMyParticipation ? "Участвую" : "Не участвую"}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      if (onViewDetails && request.id) {
                        onViewDetails(request);
                      }
                    }}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>

                  {hasMyParticipation && onCancelRequest && (
                    <Button
                      variant="outline"
                      size="icon"
                      className="text-red-600 hover:text-white"
                      onClick={() => {
                        onCancelRequest(request.id!);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
});
